// Operator-side Billing — every tenant's own view of their plan + month-by-
// month payment status. Pay button opens a Razorpay-hosted payment link
// (browser redirect). UPI AutoPay subscription is set up at signup so most
// months are recorded automatically by webhook; this page only matters for
// missed cycles, plan upgrades, and history.

import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { open_subscription_checkout } from '../lib/razorpay.js';
import { friendly_error } from '../lib/friendly-error.js';

type PlanTier = 'starter' | 'growth' | 'scale' | 'enterprise';
type BillingPeriod = 'monthly' | 'yearly';

function format_inr(paise: number): string {
  return (paise / 100).toLocaleString('en-IN');
}

interface MonthlyEntry {
  period_month: string;
  label: string;
  expected_paise: number;
  status: 'paid' | 'unpaid' | 'auto_pending';
  paid_event: {
    method: 'razorpay' | 'manual' | 'webhook';
    paid_at: string | Date;
    amount_paise: number;
  } | null;
}

interface PlanOption {
  id: string;
  tier: PlanTier;
  label: string;
  description: string;
  features: string[];
  monthly_price_paise: number;
  yearly_price_paise: number;
  current: boolean;
}

// Billing content panel — rendered inside the Settings > Billing tab.
export function BillingPanel(): ReactElement {
  const qc = useQueryClient();
  const [period_view, set_period_view] = useState<BillingPeriod>('monthly');
  const q = useQuery({
    queryKey: ['my-billing'],
    queryFn: () => orpc.tenant.billing.list_my_billing({}),
  });
  // Usage vs. the tier caps — shown so operators see how close they are to an
  // upgrade trigger (the backend enforces max_chits on creation).
  const usage_q = useQuery({
    queryKey: ['me-usage'],
    queryFn: () => orpc.me.usage({}),
  });
  const ent_q = useQuery({
    queryKey: ['me-entitlements'],
    queryFn: () => orpc.me.entitlements({}),
    staleTime: 5 * 60_000,
  });

  const start_payment_m = useMutation({
    mutationFn: (period_month: string) =>
      orpc.tenant.billing.start_payment_for_month({ period_month }),
    onSuccess: (data) => {
      // Open Razorpay in a new tab so the operator's /billing tab stays put.
      // After payment, the webhook records the billing_payment_event and the
      // operator's refresh shows the month as paid.
      if (data?.short_url) window.open(data.short_url, '_blank', 'noopener,noreferrer');
    },
  });
  const change_plan_m = useMutation({
    mutationFn: (args: { plan_id: string; period: BillingPeriod }) =>
      orpc.tenant.billing.change_my_plan(args),
    onSuccess: async (data) => {
      void qc.invalidateQueries({ queryKey: ['my-billing'] });
      if (!data?.new_razorpay_subscription_id) return;
      // Same Razorpay Checkout modal pattern as signup. Webhook flips
      // tenant.status back from pending_payment → active when the new
      // mandate is authenticated. Polling /billing after success will
      // show the new plan.
      const contact = q.data?.tenant_contact;
      try {
        await open_subscription_checkout({
          subscription_id: data.new_razorpay_subscription_id,
          name: 'ChitDesk',
          description: 'Plan change · new UPI AutoPay mandate',
          prefill: contact
            ? {
                name: contact.name,
                email: contact.email,
                ...(contact.phone ? { contact: contact.phone } : {}),
              }
            : undefined,
          on_success: () => {
            void qc.invalidateQueries({ queryKey: ['my-billing'] });
          },
          on_dismiss: () => {
            void qc.invalidateQueries({ queryKey: ['my-billing'] });
          },
        });
      } catch {
        // eslint-disable-line — Razorpay Checkout failed to load (CSP /
        // ad-blocker / network). Fall back to opening the hosted page in a
        // new tab so the operator can still complete the mandate. Backend
        // state is unaffected; the webhook race that completes the plan
        // change runs regardless of which UI flow the operator takes.
        if (data.new_checkout_url) window.open(data.new_checkout_url, '_blank', 'noopener,noreferrer');
      }
    },
  });

  const unpaid_months = useMemo(
    () => (q.data?.months ?? []).filter((m) => m.status === 'unpaid'),
    [q.data],
  );

  if (q.isLoading) return <div className="p-6 text-sm text-ink/60">Loading…</div>;
  if (q.error)
    return (
      <div className="p-6 text-sm text-bad bg-bad-soft rounded">
        {friendly_error(q.error)}
      </div>
    );
  if (!q.data) return <div className="p-6 text-sm text-ink/60">No billing data.</div>;

  const months = q.data.months as MonthlyEntry[];
  const plans = q.data.plans as PlanOption[];
  const current_period = (q.data.billing_period ?? 'monthly') as BillingPeriod;
  const current_plan = plans.find((p) => p.current);

  // Initial dropdown follows the tenant's actual period.
  const view_period = period_view ?? current_period;

  return (
    <div>
      {/* ── Renew banner ─────────────────────────────────────────── */}
      {unpaid_months.length > 0 && (
        <section className="mb-6 bg-warn-soft border border-warn/30 rounded-lg p-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-warn">
              {unpaid_months.length} unpaid month{unpaid_months.length === 1 ? '' : 's'}
            </div>
            <div className="text-xs text-ink/60 mt-0.5">
              Pay the oldest outstanding month to keep your workspace active.
            </div>
          </div>
          {unpaid_months[unpaid_months.length - 1] && (
            <button
              type="button"
              onClick={() => start_payment_m.mutate(unpaid_months[unpaid_months.length - 1]!.period_month)}
              disabled={start_payment_m.isPending}
              className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white text-sm px-4 py-2 rounded font-medium"
            >
              {start_payment_m.isPending
                ? 'Opening Razorpay…'
                : `Pay ₹${format_inr(unpaid_months[unpaid_months.length - 1]!.expected_paise)}`}
            </button>
          )}
        </section>
      )}

      {/* ── Usage vs. plan limits ───────────────────────────────── */}
      {usage_q.data && ent_q.data && (
        <section className="mb-6">
          <h2 className="text-sm uppercase tracking-wider text-ink/50 mb-3">Usage</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(() => {
              const u = usage_q.data.usage;
              const lim = ent_q.data.entitlements.limits;
              const cards: Array<{ label: string; used: number; cap: number | null; plus?: boolean }> = [
                { label: 'Active chits', used: u.active_chits, cap: lim.max_chits },
                { label: 'Members', used: u.members, cap: lim.max_members, plus: u.members_capped },
                { label: 'Staff logins', used: u.staff, cap: lim.max_staff },
              ];
              return cards.map((c) => {
                const at_limit = c.cap !== null && c.used >= c.cap;
                const pct = c.cap !== null && c.cap > 0 ? Math.min(100, Math.round((c.used / c.cap) * 100)) : 0;
                return (
                  <div key={c.label} className="border border-line rounded-lg p-4 bg-surface">
                    <div className="text-xs text-ink/50 mb-1">{c.label}</div>
                    <div className="text-xl font-semibold">
                      {c.used}{c.plus ? '+' : ''}
                      <span className="text-sm font-normal text-ink/50"> / {c.cap === null ? '∞' : c.cap}</span>
                    </div>
                    {c.cap !== null && (
                      <div className="mt-2 h-1.5 rounded-full bg-bg-elev overflow-hidden">
                        <div className={`h-full rounded-full ${at_limit ? 'bg-bad' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                    {at_limit && (
                      <div className="text-[11px] text-bad mt-1.5">At plan limit — upgrade to add more.</div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </section>
      )}

      {/* ── Plan + period switcher ──────────────────────────────── */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-ink/50">Plan</h2>
          <div className="flex items-center gap-1 bg-bg-elev rounded p-1 text-xs">
            {(['monthly', 'yearly'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set_period_view(p)}
                className={`px-3 py-1 rounded transition ${
                  view_period === p
                    ? 'bg-primary text-white font-medium'
                    : 'text-ink/60 hover:text-ink'
                }`}
              >
                {p === 'monthly' ? 'Monthly' : 'Yearly'}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {plans.map((p) => {
            const price =
              view_period === 'yearly' ? p.yearly_price_paise : p.monthly_price_paise;
            const is_current_at_view = p.current && view_period === current_period;
            return (
              <div
                key={p.id}
                className={`border rounded-lg p-4 flex flex-col ${
                  is_current_at_view ? 'border-primary bg-primary-soft' : 'border-line bg-surface'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-primary">{p.label}</span>
                  {is_current_at_view && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-primary text-white">
                      current
                    </span>
                  )}
                </div>
                <div className="text-2xl font-semibold mb-1">
                  ₹{format_inr(price)}
                  <span className="text-xs font-normal text-ink/50">
                    {' '}
                    /{view_period === 'yearly' ? 'yr' : 'mo'}
                  </span>
                </div>
                {p.description && (
                  <p className="text-xs text-ink/60 mb-3">{p.description}</p>
                )}
                {p.features.length > 0 && (
                  <ul className="text-xs text-ink/60 mb-4 space-y-0.5">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  disabled={is_current_at_view || change_plan_m.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Switch to ${p.label} (${view_period})? Your current subscription will be cancelled and a new Razorpay authentication may be required.`,
                      )
                    ) {
                      change_plan_m.mutate({ plan_id: p.id, period: view_period });
                    }
                  }}
                  className="mt-auto text-xs px-3 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {is_current_at_view ? 'Active' : `Switch to ${p.label}`}
                </button>
              </div>
            );
          })}
        </div>
        {change_plan_m.error && (
          <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
            {friendly_error(change_plan_m.error)}
          </div>
        )}
        {current_plan && (
          <p className="text-xs text-ink/50 mt-3">
            Current: <b>{current_plan.label}</b> · billed {current_period} · auto-debit via UPI AutoPay
          </p>
        )}
      </section>

      {/* ── History ───────────────────────────────────────────── */}
      <section>
        <h2 className="text-sm uppercase tracking-wider text-ink/50 mb-3">History</h2>
        {months.length === 0 ? (
          <div className="text-sm text-ink/50 italic">No billing periods yet.</div>
        ) : (
          <div className="bg-surface border border-line rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-bg-elev text-ink/60 text-xs">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Period</th>
                  <th className="text-left px-4 py-2 font-medium">Amount</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Paid on</th>
                  <th className="text-left px-4 py-2 font-medium">Method</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <tr key={m.period_month} className="border-t border-line/40">
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.label}</div>
                      <div className="text-[11px] font-mono text-ink/50">{m.period_month}</div>
                    </td>
                    <td className="px-4 py-3 text-ink/70">₹{format_inr(m.expected_paise)}</td>
                    <td className="px-4 py-3">
                      {m.status === 'paid' && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-good-soft text-good">
                          paid
                        </span>
                      )}
                      {m.status === 'auto_pending' && (
                        <span
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-info-soft text-info"
                          title="Razorpay will auto-debit this month via UPI AutoPay"
                        >
                          auto-debit
                        </span>
                      )}
                      {m.status === 'unpaid' && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-warn-soft text-warn">
                          unpaid
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink/60">
                      {m.paid_event
                        ? new Date(m.paid_event.paid_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-ink/60">{m.paid_event?.method ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {m.status === 'unpaid' && (
                        <button
                          type="button"
                          onClick={() => start_payment_m.mutate(m.period_month)}
                          disabled={start_payment_m.isPending}
                          className="text-xs px-3 py-1.5 bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white rounded"
                        >
                          {start_payment_m.isPending ? 'Opening…' : `Pay ₹${format_inr(m.expected_paise)}`}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {start_payment_m.error && (
          <div className="mt-3 px-3 py-2 bg-bad-soft text-bad text-xs rounded">
            {friendly_error(start_payment_m.error)}
          </div>
        )}
      </section>
    </div>
  );
}
